import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { projectsData } from "../data/projectsData";
import Header from "../Header";
import Footer from "../Footer";
import LiveSiteFrame, { liveHost } from "./LiveSiteFrame";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const index = projectsData.findIndex((p) => p.id === projectId);
  const project = projectsData[index];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [projectId]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="site-wrap py-32 text-center">
          <h2 className="text-4xl font-light">Project not found</h2>
          <Link to="/" className="btn-line mt-8 inline-flex">
            Back to work
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const study = project.caseStudy;
  const live = project.liveUrl && project.liveUrl !== "#";
  const next = projectsData[(index + 1) % projectsData.length];
  const extraImages = (project.images || []).filter((src) => src !== project.image);
  const blocks = [
    ["Features", project.features],
    ["Challenges", project.challenges],
    ["Solutions", project.solutions],
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="border-b border-ink/10">
        <div className="site-wrap flex flex-wrap items-center justify-between gap-4 py-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-[13px] text-ink/60 hover:text-ink"
          >
            ← All work
          </button>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-mist">
            {String(index + 1).padStart(2, "0")} / {String(projectsData.length).padStart(2, "0")}
          </p>
        </div>
      </div>

      <section className="border-b border-ink/10 py-12 lg:py-16">
        <div className="site-wrap grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-mist">
              {String(index + 1).padStart(2, "0")} — {project.type}
              {live ? ` · ${liveHost(project.liveUrl)}` : ""}
            </p>
            <h1 className="mt-3 max-w-4xl text-4xl font-light tracking-tight sm:text-6xl">
              {project.name}
            </h1>
            <p className="mt-5 max-w-2xl leading-relaxed text-ink/65">
              {project.fullDescription}
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {project.technologies.map((tech) => (
                <li
                  key={tech}
                  className="border border-ink/15 px-3 py-1.5 font-mono text-[11px] text-ink/80"
                >
                  {tech}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6 lg:col-span-4 lg:items-end lg:text-right">
            <dl className="grid grid-cols-2 gap-6 text-sm lg:grid-cols-1">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist">Duration</dt>
                <dd className="mt-1">{project.duration}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist">Role</dt>
                <dd className="mt-1">{project.role}</dd>
              </div>
            </dl>
            {live ? (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-fill"
              >
                Visit live site ↗
              </a>
            ) : null}
          </div>
        </div>
      </section>

      {live ? (
        <section className="bg-bone-dim py-10 lg:py-14">
          <div className="site-wrap">
            <div className="mb-4 flex items-center justify-between gap-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-mist">
                Live preview
              </p>
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-ink/50 hover:text-ink"
              >
                Open {liveHost(project.liveUrl)} ↗
              </a>
            </div>
            <div className="group/preview relative">
              <LiveSiteFrame
                url={project.liveUrl}
                image={project.image}
                name={project.name}
              />
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-[2]"
                aria-label={`Open ${project.name} live site`}
              >
                <span className="work-open">Visit live ↗</span>
              </a>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-bone-dim py-10 lg:py-14">
          <div className="site-wrap">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-mist">
              Internal system
            </p>
            <LiveSiteFrame image={project.image} name={project.name} />
          </div>
        </section>
      )}

      {extraImages.length > 0 ? (
        <section className="border-t border-ink/10 py-10 lg:py-14">
          <div className="site-wrap">
            <p className="label">Selected frames</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {extraImages.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`${project.name} frame ${i + 1}`}
                  className="aspect-[4/3] w-full object-cover object-top"
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="border-t border-ink/10 py-16 lg:py-20">
        <div className="site-wrap">
          <p className="label">Breakdown</p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {blocks.map(([title, items], i) => (
              <article key={title} className="process-card">
                <p className="font-mono text-[11px] text-mist">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="mt-6 text-2xl font-light">{title}</h2>
                <ul className="mt-5 space-y-3">
                  {items.map((item) => (
                    <li key={item} className="border-t border-ink/10 pt-3 text-sm leading-relaxed text-ink/65">
                      {item}
                    </li>
                  ))}
                </ul>
                <span className="process-ghost">{String(i + 1).padStart(2, "0")}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {study?.available ? (
        <section id="case-study" className="border-t border-ink/10 bg-bone-dim py-16 lg:py-24">
          <div className="site-wrap">
            <p className="label">Case study</p>
            <h2 className="mt-3 max-w-3xl text-3xl font-light tracking-tight sm:text-5xl">
              How the project came together
            </h2>
            <p className="mt-6 max-w-3xl text-lg font-light leading-snug text-ink/80">
              {study.overview}
            </p>

            <div className="mt-12 grid gap-4 lg:grid-cols-2">
              <article className="border border-ink/10 bg-white p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-mist">The problem</p>
                <p className="mt-4 leading-relaxed text-ink/70">{study.problemStatement}</p>
              </article>
              <article className="bg-ink p-8 text-white">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/45">Results</p>
                <ul className="mt-4 space-y-4">
                  {study.results.map((item, i) => (
                    <li key={item} className="flex gap-4 text-sm leading-relaxed text-white/80">
                      <span className="font-mono text-[11px] text-white/40">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              {[
                ["Research", study.userResearch],
                ["Design", study.designProcess],
                ["Build", study.technicalImplementation],
              ].map(([title, items], i) => (
                <article key={title} className="border border-ink/10 bg-white p-7">
                  <p className="font-mono text-[11px] text-mist">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 text-xl font-light">{title}</h3>
                  <ul className="mt-5 space-y-3">
                    {items.map((item) => (
                      <li key={item} className="text-sm leading-relaxed text-ink/60">
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="bg-ink py-16 text-white lg:py-20">
        <div className="site-wrap flex flex-col gap-10 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/40">Next</p>
            <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-5xl">
              Have a website or system in mind?
            </h2>
            {next && next.id !== project.id ? (
              <p className="mt-4 text-sm text-white/50">
                Or keep looking —{" "}
                <Link to={`/project/${next.id}`} className="text-white underline-offset-4 hover:underline">
                  {next.name} →
                </Link>
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <Link to="/#contact" className="btn-on-dark">
              Book A Call
            </Link>
            <Link to="/#projects" className="text-[13px] text-white/55 hover:text-white">
              More work
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
